<template>
  <SectionTemplate
    title="近千款影视级场景 无论什么主题都能匹配"
    desc="海量高质量场景库，覆盖电商带货、企业宣传、教育培训、财经资讯等多种类型，满足您多样化的视频创作需求。"
    class="page-studio-card"
  >
    <div class="page-studio-card__list">
      <StudioCard
        v-for="(studio, idx) in studioList"
        v-bind="studio"
        :key="idx"
        @use-studio="handleUseStudio"
      />
    </div>
    <ArrowBtn
      text="查看更多场景"
      variant="border-white-middle"
      class="page-studio-card__btn"
      @click="handleToScene"
    />
  </SectionTemplate>
</template>

<script setup lang="ts">
import { getFeUrl } from '@/utils/url';
import { addOpus } from '~/api';
import ArrowBtn from '~/design/components/buttons/arrow-btn.vue';
import StudioCard, { type IStudioCard } from '~/design/components/studio-card/index.vue';
import SectionTemplate from '~/design/widgets/landing-section/cn-template.vue';

const handleToScene = () => {
  window.open('/workspace/scene', '_self');
};

const handleUseStudio = (identity: string) => {
  const create_data = {
    map_identity: identity,
  };

  // 未登录跳转登录
  const userInfo = useUserInfo();
  if (!userInfo.value) {
    // 接入场景和角色等接口
    loginTask.add({
      type: IAfterLoginActionType.CREATE_WORK_WITH_PRESET,
      payload: { create_data },
    });
    navigateTo(
      `/login?redirect=${encodeURIComponent(encodeURIComponent(`${location.origin}/workspace`))}`,
    );
    return;
  }
  return addOpus(create_data as any).then(result => {
    if (result) {
      const data = result.data as any;
      window.open(`/editor/${data.id}/${data.secret_id}`, '_blank');
    }
  });
};

const studioList: IStudioCard[] = [
  {
    title: '白色流线概念风发布会场',
    description:
      '这是一个带有大屏幕的舞台场景，简约科技感的视觉风格，可以让视频非常吸引观众的注意力，可以用于制作高级的发布会讲解视频。',
    coverUrl: getFeUrl('/youyan-platform/scene/scene-library/white-streamline-concept-venue.png'),
    tags: ['大舞台', '科幻抽象', '企业营销'],
    identity: 'lstage_simple_white_01',
  },
  {
    title: '淡粉简约美容中心',
    description:
      '温馨干净的室内场景，伴随简约的软装，让整个空间非常舒适。非常适用于医疗医美、校园心理疗愈、瑜伽教学等视频内容的创作。',
    coverUrl: getFeUrl('/youyan-platform/scene/scene-library/light-pink-beauty-center.png'),
    tags: ['温馨简约', '医疗医美', '企业营销'],
    identity: 'bust_simple_beauty_center_02',
  },
  {
    title: '天山雪湖',
    description:
      '雪天的户外场景，让观众一下子就能感觉到自然地视觉，您可以使用这个场景创作旅行日常分享，或者用于户外产品营销。',
    coverUrl: getFeUrl('/youyan-platform/scene/scene-library/tianshan-snow-lake.png'),
    tags: ['户外场景', '清新自然', '本地生活'],
    identity: 'bust_nature_snow_mountain_01',
  },
  {
    title: '玻璃斜顶木屋',
    description:
      '这是温馨的室内场景，阳光下带窗户的阁楼带着独特的情调，让人感到温馨。适用于创作居家产品种草视频或知识分享视频。',
    coverUrl: getFeUrl('/youyan-platform/scene/scene-library/glass-sloped-roof-cabin.png'),
    tags: ['室内坐姿', '温馨居家', '个人生活'],
    identity: 'bust_home_pitched_top_01',
  },
  {
    title: '纸飞机黑板报',
    description:
      '简单趣味的平面背景，有多种校园设计元素排布，兼具趣味与知识感。适用于创作儿童教育微课视频，或者知识分享视频。',
    coverUrl: getFeUrl('/youyan-platform/scene/scene-library/paper-airplane-blackboard.png'),
    tags: ['2D背景', '卡通童趣', '教育教学'],
    identity: 'ppt_tech_k12_04',
  },
  {
    title: '医院服务中心',
    description:
      '阳光透过窗户照在医院的地上，让人感到非常温馨，简约的场景也具有专业感。可用于制作医疗培训视频，或患者术后康复指南视频。',
    coverUrl: getFeUrl('/youyan-platform/scene/scene-library/hospital-service-center.png'),
    tags: ['现代室内', '医疗培训', '患者服务'],
    identity: 'bust_modern_hospital_02',
  },
  {
    title: '现代都市演播厅',
    description:
      '非常专业的新闻直播场景，带有播放素材的屏幕，以及简约的台桌，用来制作新闻播报类视频非常合适，也可以用于官方信息播报。',
    coverUrl: getFeUrl('/youyan-platform/scene/scene-library/modern-urban-studio.png'),
    tags: ['广电传媒', '现代主义', '新闻播报'],
    identity: 'telestudio_simple_red_01_sit',
  },
  {
    title: '高新智能业务大厅',
    description:
      '这是一个简约且科幻的空间，曲线的建筑结构能够让视频非常高级有质感，非常适用于官方的信息播报，或者科技产品的讲解视频。',
    coverUrl: getFeUrl('/youyan-platform/scene/scene-library/high-tech-intelligent-hall.png'),
    tags: ['科技风', '简约室内', '企业培训'],
    identity: 'bust_modern_curve_01',
  },
  {
    title: '清晨阳光绿植房',
    description:
      '这是一个竖屏的居家场景，绿色的盆栽环绕在躺椅周边，非常松弛有质感，无论是居家产品种草，还是生活知识分享都非常合适。',
    coverUrl: getFeUrl('/youyan-platform/scene/scene-library/morning-sunlight-green-room.png'),
    tags: ['温馨室内', '清新自然', '社媒运营'],
    identity: 'bust_nature_green_room_01',
    isPortrait: true,
  },
];
</script>

<style lang="scss" scoped>
.page-studio-card__list {
  display: grid;
  width: 100%;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.page-studio-card__btn {
  margin-top: 80px;
}
</style>
